import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTasks, getUsers, getDepartments, getAreas } from '../utils/api';
import TaskCard from '../components/TaskCard';
import TaskDrawer from '../components/TaskDrawer';
import AdvancedFilters from '../components/AdvancedFilters';
import { isOverdue, isToday, isThisWeek } from '../utils/dateHelpers';

const CLOSED = ['Aprobada / Cerrada', 'No realizada'];

// ─── Acordeón por responsabilidad ────────────────────────────────────────────
const ResponsabilidadGroup = ({ name, active, closed, users, onSelect, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const total = active.length + closed.length;

  return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.08)',
      boxShadow: open ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
    }}>
      {/* Header colapsable */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors"
        style={{ background: open ? 'rgba(0,208,132,0.04)' : 'transparent' }}
      >
        <svg
          className="flex-shrink-0 transition-transform"
          style={{ width: 16, height: 16, color: '#94A3B8', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#94A3B8' }}>
            Responsabilidad
          </p>
          <p className="text-sm font-bold truncate" style={{ color: '#0F172A' }}>{name}</p>
        </div>
        <span className="flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full" style={{
          background: open ? 'rgba(0,208,132,0.12)' : 'rgba(0,0,0,0.05)',
          color: open ? '#00A86B' : '#64748B',
        }}>
          {total} tarea{total !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Contenido expandido */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="pt-2 space-y-2">
                {/* Tareas activas */}
                {active.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    users={users}
                    onClick={() => onSelect(task)}
                  />
                ))}
                {/* Tareas cerradas — al final, atenuadas */}
                {closed.length > 0 && (
                  <div className="space-y-2 opacity-50">
                    {closed.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        users={users}
                        onClick={() => onSelect(task)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────
const MyTasks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const urlFilter = searchParams.get('filter');
  const urlStatus = searchParams.get('status');
  const urlDepartmentId = searchParams.get('department_id');
  const urlPriority = searchParams.get('priority');

  useEffect(() => {
    loadData();
    if (urlFilter) setActiveFilter(urlFilter);
    else if (urlStatus) setActiveFilter('custom');
  }, []);

  const clearFilters = () => {
    setSearchParams({});
    setActiveFilter('all');
    setSearchQuery('');
  };

  const loadData = async () => {
    try {
      const tasksRes = await getTasks();
      setTasks(tasksRes.data);
      try {
        const [usersRes, deptsRes, areasRes] = await Promise.all([
          getUsers(), getDepartments(), getAreas()
        ]);
        setUsers(usersRes.data);
        setDepartments(deptsRes.data);
        setAreas(areasRes.data);
      } catch {}
    } catch {}
    finally { setLoading(false); }
  };

  const handleApplyAdvancedFilters = (advFilters) => {
    const params = new URLSearchParams();
    if (advFilters.status.length) params.append('status', advFilters.status.join(','));
    if (advFilters.priority.length) params.append('priority', advFilters.priority.join(','));
    if (advFilters.department_id.length) params.append('department_id', advFilters.department_id.join(','));
    if (advFilters.responsible_user_id.length) params.append('responsible_user_id', advFilters.responsible_user_id.join(','));
    if (advFilters.waiting_type.length) params.append('waiting_type', advFilters.waiting_type.join(','));
    if (advFilters.overdue_only) params.append('filter', 'overdue');
    setSearchParams(params);
  };

  // Devuelve las tareas filtradas. Cuando filter='all' y sin búsqueda,
  // incluye cerradas (se muestran al final de cada grupo).
  const getFilteredTasks = (includeClosed = false) => {
    let filtered = tasks;

    if (urlDepartmentId) filtered = filtered.filter(t => t.responsible_user_id === urlDepartmentId);
    if (urlPriority) filtered = filtered.filter(t => t.priority === urlPriority);
    if (urlStatus) filtered = filtered.filter(t => t.status === urlStatus);

    if (urlFilter === 'critical_24h') {
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => t.priority === 'Crítica' && t.status !== 'Aprobada / Cerrada' && t.due_date && new Date(t.due_date) <= tomorrow && new Date(t.due_date) >= new Date());
    } else if (urlFilter === 'due_soon') {
      const in48h = new Date(Date.now() + 48 * 60 * 60 * 1000);
      filtered = filtered.filter(t => t.status !== 'Aprobada / Cerrada' && t.due_date && new Date(t.due_date) >= new Date() && new Date(t.due_date) <= in48h);
    } else if (urlFilter === 'blocked_48h') {
      const twoDaysAgo = new Date(Date.now() - 48 * 60 * 60 * 1000);
      filtered = filtered.filter(t => t.status === 'Bloqueada' && new Date(t.updated_at) <= twoDaysAgo);
    } else if (urlFilter === 'overdue') {
      filtered = filtered.filter(t => isOverdue(t.due_date, t.status));
    }

    if (!urlFilter && !urlStatus && !urlPriority && !urlDepartmentId) {
      switch (activeFilter) {
        case 'today':    filtered = filtered.filter(t => isToday(t.due_date)); break;
        case 'week':     filtered = filtered.filter(t => isThisWeek(t.due_date)); break;
        case 'overdue':  filtered = filtered.filter(t => isOverdue(t.due_date, t.status)); break;
        case 'critical': filtered = filtered.filter(t => t.priority === 'Crítica' && !CLOSED.includes(t.status)); break;
        case 'blocked':  filtered = filtered.filter(t => t.status === 'Bloqueada'); break;
        case 'waiting_client':   filtered = filtered.filter(t => t.status === 'En espera de cliente'); break;
        case 'waiting_internal': filtered = filtered.filter(t => t.status === 'En espera de usuario/Presidencia'); break;
        case 'review':   filtered = filtered.filter(t => t.status === 'Lista para revisión'); break;
        default:
          if (!includeClosed) filtered = filtered.filter(t => !CLOSED.includes(t.status));
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q)) ||
        (t.responsabilidad && t.responsabilidad.toLowerCase().includes(q))
      );
    }

    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'due_date') {
        if (!a.due_date) return 1; if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      } else if (sortBy === 'priority') {
        const o = { 'Crítica': 0, 'Alta': 1, 'Media': 2, 'Baja': 3 };
        return o[a.priority] - o[b.priority];
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return filtered;
  };

  // En vista "todas" agrupamos incluyendo cerradas al final de cada grupo
  const isDefaultView = activeFilter === 'all' && !urlFilter && !urlStatus && !urlPriority && !urlDepartmentId;
  const allFiltered    = getFilteredTasks(isDefaultView);  // includes closed when default
  const activeFiltered = getFilteredTasks(false);          // only active (for count badge)

  // Agrupar por responsabilidad
  const groups = {};
  allFiltered.forEach(task => {
    const key = task.responsabilidad || 'Sin responsabilidad';
    if (!groups[key]) groups[key] = { active: [], closed: [] };
    if (CLOSED.includes(task.status)) groups[key].closed.push(task);
    else groups[key].active.push(task);
  });
  const groupKeys = Object.keys(groups).sort();

  const filterChips = [
    { key: 'all',              label: 'Todas',         count: tasks.filter(t => !CLOSED.includes(t.status)).length },
    { key: 'today',            label: 'Hoy',            count: tasks.filter(t => isToday(t.due_date)).length },
    { key: 'week',             label: 'Semana',         count: tasks.filter(t => isThisWeek(t.due_date)).length },
    { key: 'overdue',          label: 'Vencidas',       count: tasks.filter(t => isOverdue(t.due_date, t.status)).length },
    { key: 'critical',         label: 'Críticas',       count: tasks.filter(t => t.priority === 'Crítica' && !CLOSED.includes(t.status)).length },
    { key: 'blocked',          label: 'Bloqueadas',     count: tasks.filter(t => t.status === 'Bloqueada').length },
    { key: 'waiting_client',   label: 'Espera Cliente', count: tasks.filter(t => t.status === 'En espera de cliente').length },
    { key: 'waiting_internal', label: 'Espera Interna', count: tasks.filter(t => t.status === 'En espera de usuario/Presidencia').length },
    { key: 'review',           label: 'En Revisión',    count: tasks.filter(t => t.status === 'Lista para revisión').length },
  ];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Cargando tareas...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#F1F5F9' }}>

      {/* Header */}
      <div className="sticky top-14 md:top-0 z-10 px-4 py-4" style={{
        background: 'rgba(10,26,20,0.92)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1.5px solid rgba(0,208,132,0.25)',
      }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black" style={{
              background: 'linear-gradient(135deg,#00D084,#00FFB3)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Mis Tareas
            </h1>
            <p className="text-xs font-semibold mt-0.5" style={{ color: '#A0F0D0' }}>
              {activeFiltered.length} activa{activeFiltered.length !== 1 ? 's' : ''}
              {isDefaultView && groups && Object.keys(groups).length > 0 && ` · ${Object.keys(groups).length} responsabilidades`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/tasks/create')} className="btn-primary flex items-center gap-1.5 text-sm">
              <span className="text-base">+</span> Nueva
            </button>
            <button onClick={() => setShowAdvancedFilters(true)} className="btn-secondary text-sm">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Filtros activos URL */}
      {(urlFilter || urlStatus || urlDepartmentId || urlPriority) && (
        <div className="px-4 py-2.5 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground">Filtros:</span>
            {urlFilter && <span className="chip-decko chip-active text-xs">{urlFilter}</span>}
            {urlStatus && <span className="chip-decko chip-active text-xs">{urlStatus}</span>}
            {urlPriority && <span className="chip-decko chip-active text-xs">{urlPriority}</span>}
            <button onClick={clearFilters} className="ml-auto btn-secondary text-xs py-1">Limpiar</button>
          </div>
        </div>
      )}

      {/* Búsqueda + Orden */}
      <div className="px-4 py-3 max-w-3xl mx-auto">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar tareas o responsabilidades..."
            className="input-decko flex-1 text-sm"
          />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="input-decko text-sm min-w-[130px]">
            <option value="created_at">Recientes</option>
            <option value="due_date">Fecha límite</option>
            <option value="priority">Prioridad</option>
          </select>
        </div>
      </div>

      {/* Chips de filtro */}
      <div className="px-4 pb-3 overflow-x-auto max-w-3xl mx-auto">
        <div className="flex gap-2 min-w-max">
          {filterChips.map(f => (
            <button key={f.key} onClick={() => { setActiveFilter(f.key); setSearchParams({}); }}
              className={`chip-decko text-xs ${activeFilter === f.key && !urlFilter ? 'chip-active' : 'chip-inactive'}`}>
              {f.label} {f.count > 0 && `(${f.count})`}
            </button>
          ))}
        </div>
      </div>

      {/* Lista agrupada */}
      <div className="px-4 pb-8 max-w-3xl mx-auto space-y-3">
        {groupKeys.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
            <div className="text-4xl mb-3">🎉</div>
            <p className="font-semibold" style={{ color: '#1E293B' }}>
              {searchQuery ? 'Sin resultados' : 'Sin tareas en este filtro'}
            </p>
            <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
              {searchQuery ? 'Intenta con otra búsqueda' : 'Prueba con otro filtro'}
            </p>
          </div>
        ) : (
          groupKeys.map((key, idx) => (
            <ResponsabilidadGroup
              key={key}
              name={key}
              active={groups[key].active}
              closed={groups[key].closed}
              users={users}
              onSelect={setSelectedTask}
              defaultOpen={idx === 0}  // primer grupo abierto por defecto
            />
          ))
        )}
      </div>

      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onUpdate={loadData}
        />
      )}

      <AdvancedFilters
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApply={handleApplyAdvancedFilters}
        areas={areas}
        departments={departments}
        users={users}
      />
    </div>
  );
};

export default MyTasks;
